import { Checkbox, Radio, RadioGroup, Textarea, Select, SelectItem, Input } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	type DbUser,
	type StepBackground,
	createBackgroundSchema,
	FIELD_LIMITS,
	requiresVideoIntro,
	straightenApostrophes,
} from '@leangap/types'
import { useEffect, useRef, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { TEXTAREA_MAX_CHARS, TEXTAREA_MIN_ROWS } from '@/constants/form'
import { HowHearAboutUsQuestion } from '@/components/HowHearAboutUsQuestion'

import StepHeader from '@/components/account/StepHeader'
import OutsideLabel from '@/components/account/OutsideLabel'
import { useStepStatusSync } from '@/store/useStepStatusSync'
import { appStore, useAppStore, isApplicationLocked } from '@/store'
import SectionFormTitles from '@/components/account/SectionFormTitles'
import { useFormVisitValidation } from '@/hooks/useFormVisitValidation'

import { REASONS, ENTREPRENEURIAL_TYPES } from '@/constants/background'

export default function BackgroundStep() {
	const { user } = useAppStore()
	if (!user) return null
	return <BackgroundForm user={user} />
}

function BackgroundForm({ user }: { user: DbUser }) {
	const isSubmitted = isApplicationLocked(user.application_process)

	// Subscribe to store's current country value (updates immediately when General section changes)
	// This is different from user.country which only updates after save
	const storeCountry = useAppStore().user?.country

	// Determine if video intro is required based on user's country (use store value for instant updates)
	const needsVideoIntro = requiresVideoIntro(storeCountry)

	// Store the current schema requirement in a ref so the resolver always uses the latest value
	const needsVideoIntroRef = useRef(needsVideoIntro)
	needsVideoIntroRef.current = needsVideoIntro

	// Create a dynamic resolver that always uses the current needsVideoIntro value
	const dynamicResolver = useMemo(() => {
		return (values: StepBackground, context: unknown, options: unknown) => {
			const schema = createBackgroundSchema(needsVideoIntroRef.current)
			return zodResolver(schema)(values, context, options as any)
		}
	}, [])

	// RHF defaults from store
	const toDefaults = (u: DbUser): StepBackground => ({
		share_proud: u.share_proud ?? '',
		// Older drafts may hold a curly apostrophe; match the straight REASONS values
		share_reason_to_apply: straightenApostrophes(u.share_reason_to_apply ?? ''),
		video_intro: u.video_intro ?? '',
		video_intro_is_public: u.video_intro_is_public ?? false,
		share_what_want_to_learn: u.share_what_want_to_learn ?? '',
		share_practical_skills: u.share_practical_skills ?? '',
		share_extracurricular_activities: u.share_extracurricular_activities ?? '',
		share_entrepreneurial_type: u.share_entrepreneurial_type ?? '',
		share_what_makes_teenager: u.share_what_makes_teenager ?? '',
		share_how_hear_about_us: u.share_how_hear_about_us ?? '',
		share_how_hear_about_us_extra: u.share_how_hear_about_us_extra ?? '',
		share_something_to_know_about: u.share_something_to_know_about ?? '',
	})

	const form = useForm<StepBackground>({
		resolver: dynamicResolver,
		defaultValues: toDefaults(user),
		mode: 'onChange',
		criteriaMode: 'all',
	})

	const {
		register,
		watch,
		setValue,
		clearErrors,
		trigger,
		formState: { errors, isValid, isDirty },
	} = form

	useEffect(() => {
		const main = watch('share_how_hear_about_us')
		if (!main) {
			clearErrors('share_how_hear_about_us')
			clearErrors('share_how_hear_about_us_extra')
		}
	}, [clearErrors, watch])

	// Re-validate when needsVideoIntro changes (e.g., when country changes in General section)
	// This ensures the Background section's validity is updated when the video_intro requirement changes
	useEffect(() => {
		// Trigger validation for all fields when the video requirement changes
		trigger()
	}, [needsVideoIntro, trigger])

	const visited = useFormVisitValidation('background', form)
	useStepStatusSync('background', isValid, isDirty, form.formState.isSubmitted)

	const videoIntroIsPublic = watch('video_intro_is_public')
	const touchedFields = (form.formState.touchedFields as unknown as Record<string, unknown>) || {}
	const shouldShowErrorFor = (field: keyof StepBackground) =>
		form.formState.isSubmitted || visited || Boolean(touchedFields[field])
	const showVideoPublicDanger =
		needsVideoIntro &&
		!videoIntroIsPublic &&
		(form.formState.isSubmitted || Boolean(touchedFields['video_intro_is_public']))

	useEffect(() => {
		if (isSubmitted) return
		const sub = watch((values) => {
			const v = values as StepBackground
			appStore.patch({
				share_proud: v.share_proud ?? null,
				share_reason_to_apply: v.share_reason_to_apply ?? null,
				video_intro: v.video_intro ?? null,
				video_intro_is_public: v.video_intro_is_public ?? false,
				share_what_want_to_learn: v.share_what_want_to_learn ?? null,
				share_practical_skills: v.share_practical_skills ?? null,
				share_extracurricular_activities: v.share_extracurricular_activities ?? null,
				share_entrepreneurial_type: v.share_entrepreneurial_type ?? null,
				share_what_makes_teenager: v.share_what_makes_teenager ?? null,
				share_how_hear_about_us: v.share_how_hear_about_us ?? null,
				share_how_hear_about_us_extra: v.share_how_hear_about_us_extra ?? null,
				share_something_to_know_about: v.share_something_to_know_about ?? null,
			})
		})
		return () => sub.unsubscribe()
	}, [watch, isSubmitted])

	const practicalWrapperRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		const container = practicalWrapperRef.current
		if (!container || typeof window === 'undefined') return

		const labelEl = container.querySelector('label')
		if (!labelEl) return

		const helperEl = document.createElement('span')
		helperEl.className = 'text-foreground-500 text-sm mb-2'
		helperEl.innerHTML =
			'Leadership, communication, listening, organization, critical thinking, and similar, <span class="underline">are not considered practical skills</span>.'

		labelEl.insertAdjacentElement('afterend', helperEl)

		return () => {
			helperEl.remove()
		}
	}, [])

	return (
		<>
			<StepHeader
				title="Your Background"
				lead={
					<>
						Please answer the questions below in <strong>3 or more sentences</strong>. There is a
						2,000 character limit for each question
					</>
				}
			/>
			<form className="mt-8 space-y-10 pb-12">
				<section>
					<Textarea
						labelPlacement="outside"
						id="share_proud"
						label={
							<OutsideLabel>What's the latest thing you did that you are proud of?</OutsideLabel>
						}
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						isRequired
						isReadOnly={isSubmitted}
						{...register('share_proud')}
						isInvalid={!!errors.share_proud}
						aria-invalid={!!errors.share_proud}
						aria-describedby={errors.share_proud ? 'share_proud_error' : undefined}
					/>
					{errors.share_proud && (
						<p className="text-danger-500 mt-1 text-xs" id="share_proud_error">
							{errors.share_proud.message}
						</p>
					)}
				</section>

				<section>
					<div className="flex flex-col gap-1">
						<label className="block leading-6 font-semibold" htmlFor="share_reason_to_apply_label">
							What's the #1 reason you want to apply to Leangap?
							<span className="text-danger text-xs">*</span>
						</label>
						<div className="text-foreground-500 flex flex-col gap-1 text-sm">
							<p>
								This will <span className="underline"> not affect</span> your chances of admission.
								<span className="underline">Research purposes only.</span>
							</p>
							<p>Choose the most applicable one.</p>
						</div>
					</div>

					<RadioGroup
						id="share_reason_to_apply"
						aria-labelledby="share_reason_to_apply_label"
						value={watch('share_reason_to_apply')}
						onValueChange={(val) =>
							setValue('share_reason_to_apply', val, { shouldValidate: true })
						}
						isReadOnly={isSubmitted}
						className="mt-2 flex flex-col gap-1"
					>
						{REASONS.map((r) => (
							<Radio key={r} value={r}>
								<span className="ml-1">{r}</span>
							</Radio>
						))}
					</RadioGroup>
					{errors.share_reason_to_apply && (
						<p className="text-danger-500 mt-1 text-xs">{errors.share_reason_to_apply.message}</p>
					)}
				</section>

				{needsVideoIntro && (
					<section>
						<div className="flex flex-col gap-1">
							<label className="block leading-6 font-semibold" htmlFor="video_intro">
								Video Introduction
								<span className="text-danger text-xs">*</span>
							</label>
							<div className="text-foreground-500 text-sm">
								<p>
									Based on your answer to the previous question, please record a short video that
									explains your reasoning. Paste the link below (Loom, unlisted YouTube, Google
									Drive, Dropbox, etc.). The video must be at least 30 seconds and no longer than 1
									minute. It is also your responsibility to make sure the video can be accessed by
									anyone with the link.
								</p>
							</div>
						</div>
						<Input
							id="video_intro"
							type="url"
							placeholder="Enter a link"
							isRequired
							isReadOnly={isSubmitted}
							maxLength={FIELD_LIMITS.url}
							{...register('video_intro')}
							isInvalid={!!errors.video_intro}
							aria-invalid={!!errors.video_intro}
							aria-describedby={errors.video_intro ? 'video_intro_error' : undefined}
							className="mt-2"
						/>
						{errors.video_intro && (
							<p className="text-danger-500 mt-1 text-xs" id="video_intro_error">
								{errors.video_intro.message}
							</p>
						)}
						<Checkbox
							className="mt-2"
							isSelected={!!videoIntroIsPublic}
							onValueChange={(value) =>
								setValue('video_intro_is_public', value, {
									shouldValidate: true,
									shouldDirty: true,
								})
							}
							color={showVideoPublicDanger ? 'danger' : 'default'}
							classNames={{
								// Multi-line label: align the box with the first line instead of centering.
								base: 'items-start',
								wrapper: 'mt-0.5',
								label: showVideoPublicDanger ? 'text-danger' : undefined,
							}}
							isDisabled={isSubmitted}
						>
							I verified in an incognito/private window that my link is publicly accessible. I
							understand my application may be rejected if the link cannot be opened.
						</Checkbox>
						{errors.video_intro_is_public && shouldShowErrorFor('video_intro_is_public') && (
							<p className="text-danger mt-1 text-sm" id="video_intro_is_public_error">
								{errors.video_intro_is_public.message}
							</p>
						)}
					</section>
				)}

				<section>
					<Textarea
						labelPlacement="outside"
						id="share_what_want_to_learn"
						label={
							<OutsideLabel>
								What is the most important thing you want to learn at Leangap?
							</OutsideLabel>
						}
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						isRequired
						isReadOnly={isSubmitted}
						{...register('share_what_want_to_learn')}
						isInvalid={!!errors.share_what_want_to_learn}
						aria-invalid={!!errors.share_what_want_to_learn}
						aria-describedby={
							errors.share_what_want_to_learn ? 'share_what_want_to_learn_error' : undefined
						}
						className="mt-2"
					/>
					{errors.share_what_want_to_learn && (
						<p className="text-danger-500 mt-1 text-xs" id="share_what_want_to_learn_error">
							{errors.share_what_want_to_learn.message}
						</p>
					)}
				</section>

				<section>
					<div ref={practicalWrapperRef}>
						<Textarea
							labelPlacement="outside"
							id="share_practical_skills"
							label={
								<OutsideLabel>
									If you were accepted into Leangap, what practical skills would you provide for
									your team?
								</OutsideLabel>
							}
							maxLength={TEXTAREA_MAX_CHARS}
							minRows={TEXTAREA_MIN_ROWS}
							isRequired
							isReadOnly={isSubmitted}
							{...register('share_practical_skills')}
							isInvalid={!!errors.share_practical_skills}
							aria-invalid={!!errors.share_practical_skills}
							aria-describedby={
								errors.share_practical_skills ? 'share_practical_skills_error' : undefined
							}
							className="mt-2"
						/>
					</div>
					{errors.share_practical_skills && (
						<p className="text-danger-500 mt-1 text-xs" id="share_practical_skills_error">
							{errors.share_practical_skills.message}
						</p>
					)}
				</section>

				<section>
					<Textarea
						labelPlacement="outside"
						id="share_extracurricular_activities"
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						label={
							<OutsideLabel>
								Tell us about any extracurricular activities you are involved in.
							</OutsideLabel>
						}
						isRequired
						isReadOnly={isSubmitted}
						{...register('share_extracurricular_activities')}
						isInvalid={!!errors.share_extracurricular_activities}
						aria-invalid={!!errors.share_extracurricular_activities}
						aria-describedby={
							errors.share_extracurricular_activities
								? 'share_extracurricular_activities_error'
								: undefined
						}
						className="mt-2"
					/>
					{errors.share_extracurricular_activities && (
						<p className="text-danger-500 mt-1 text-xs" id="share_extracurricular_activities_error">
							{errors.share_extracurricular_activities.message}
						</p>
					)}
				</section>

				<section>
					<SectionFormTitles>Entrepreneurial Type</SectionFormTitles>
					<p className="mt-2">Read these different entrepreneurial types:</p>
					<div className="mt-4 mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="border-default-200 bg-default-100 relative overflow-hidden rounded-xl border p-4">
							<div className="absolute top-0 left-0 h-full w-1 bg-purple-500" />
							<div className="absolute top-0 left-0 h-16 w-32 bg-gradient-to-br from-purple-500/20 to-transparent" />
							<h4 className="mb-2 font-semibold text-purple-400">BRANDER</h4>
							<p className="text-foreground/90 text-sm">
								You're all about shaping the product or service vibe with killer branding, graphics,
								and ads. Your creative flair sets trends and makes the product look and feel just
								right.
							</p>
						</div>
						<div className="border-default-200 bg-default-100 relative overflow-hidden rounded-xl border p-4">
							<div className="bg-success-400 absolute top-0 left-0 h-full w-1" />
							<div className="from-success-400/20 absolute top-0 left-0 h-16 w-32 bg-gradient-to-br to-transparent" />
							<h4 className="text-success-400 mb-2 font-semibold">BRAIN</h4>
							<p className="text-foreground/90 text-sm">
								You're the detective of the crew, chatting everyday with people and potential
								customers, spotting trends, and figuring out what's going on. You enjoy joining
								social groups and clubs to learn what users truly need.
							</p>
						</div>
						<div className="border-default-200 bg-default-100 relative overflow-hidden rounded-xl border p-4">
							<div className="bg-primary-500 absolute top-0 left-0 h-full w-1" />
							<div className="from-primary-500/20 absolute top-0 left-0 h-16 w-32 bg-gradient-to-br to-transparent" />
							<h4 className="text-primary-400 mb-2 font-semibold">BUILDER</h4>
							<p className="text-foreground/90 text-sm">
								You're the tech wizard who brings product ideas to life. Whether it's crafting a
								physical prototype or coding up an app or website, you're the one making sure the
								idea becomes reality.
							</p>
						</div>
						<div className="border-default-200 bg-default-100 relative overflow-hidden rounded-xl border p-4">
							<div className="bg-warning-500 absolute top-0 left-0 h-full w-1" />
							<div className="from-warning-500/20 absolute top-0 left-0 h-16 w-32 bg-gradient-to-br to-transparent" />
							<h4 className="text-warning-400 mb-2 font-semibold">BUSINESS DEVELOPER</h4>
							<p className="text-foreground/90 text-sm">
								You're the project coordinator who guards the company's numbers using your skills in
								data, math, and logic to keep an eye on the finances and drive sales to boost the
								bottom line.
							</p>
						</div>
					</div>

					<div className="mt-8 flex">
						<Select
							id="share_entrepreneurial_type"
							isRequired
							selectedKeys={[watch('share_entrepreneurial_type')]}
							onSelectionChange={(keys) => {
								const key = Array.isArray(keys)
									? keys[0]
									: typeof keys === 'string'
										? keys
										: (keys?.currentKey ?? '')
								// Always mark as dirty and validate, especially when clearing
								setValue('share_entrepreneurial_type', key, {
									shouldValidate: true,
									shouldDirty: true,
								})
							}}
							isInvalid={!!errors.share_entrepreneurial_type}
							errorMessage={errors.share_entrepreneurial_type?.message}
							className="w-full"
							isDisabled={isSubmitted}
							label={<OutsideLabel>Which entrepreneurial type feels most like you?</OutsideLabel>}
							labelPlacement="outside"
						>
							<>
								<SelectItem key="">Select...</SelectItem>
								{ENTREPRENEURIAL_TYPES.map((t) => (
									<SelectItem key={t}>{t}</SelectItem>
								))}
							</>
						</Select>
					</div>
				</section>

				<section>
					<Textarea
						labelPlacement="outside"
						id="share_what_makes_teenager"
						label={<OutsideLabel>Tell us what makes you a unique teenager.</OutsideLabel>}
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						isRequired
						isReadOnly={isSubmitted}
						{...register('share_what_makes_teenager')}
						isInvalid={!!errors.share_what_makes_teenager}
						aria-invalid={!!errors.share_what_makes_teenager}
						aria-describedby={
							errors.share_what_makes_teenager ? 'share_what_makes_teenager_error' : undefined
						}
					/>
				</section>

				<section>
					<HowHearAboutUsQuestion
						register={register}
						watch={watch}
						setValue={setValue}
						clearErrors={clearErrors}
						trigger={trigger}
						errors={errors}
						name="share_how_hear_about_us"
						extraName="share_how_hear_about_us_extra"
						isSubmitted={isSubmitted}
					/>
				</section>

				<section>
					<Textarea
						labelPlacement="outside"
						id="share_something_to_know_about"
						label={
							<OutsideLabel>
								Is there anything else you would like our admissions team to know about?
							</OutsideLabel>
						}
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						isReadOnly={isSubmitted}
						{...register('share_something_to_know_about')}
						isInvalid={!!errors.share_something_to_know_about}
						aria-invalid={!!errors.share_something_to_know_about}
						aria-describedby={
							errors.share_something_to_know_about
								? 'share_something_to_know_about_error'
								: undefined
						}
					/>
				</section>
			</form>
		</>
	)
}
